/**
 * PreRegistrationsPanel - Porteiro interface for visitor pre-registration approval
 *
 * Shows pending pre-registrations and allows porteiros to approve or reject them.
 * Auto-refreshes every 30 seconds and shows badge notification count.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Badge,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Home, Phone, Clock, Check, X, ZoomIn } from 'lucide-react';
import {
  CheckCircle,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  QrCode as QrCodeIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCustomer } from '../../../contexts/CustomerContext';
import {
  getPendingPreRegistrations,
  approvePreRegistration,
  rejectPreRegistration,
  getActiveQRCode,
  getAllQRCodes,
  PreRegistration,
  QRCode,
} from '../../../services/preRegistrationApi';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';

interface PreRegistrationsPanelProps {
  open: boolean;
  onClose: () => void;
  onValidatePreRegistration?: (preReg: PreRegistration) => void;
  onNewRegistration?: (token: string) => void;
  customerId?: string;
  inline?: boolean;
  lightTheme?: boolean;
}

const PreRegistrationsPanel: React.FC<PreRegistrationsPanelProps> = ({ open, onClose, onValidatePreRegistration, onNewRegistration, customerId: customerIdProp, inline, lightTheme }) => {
  const { selectedCustomer } = useCustomer();

  // State
  const [preRegistrations, setPreRegistrations] = useState<PreRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedPreReg, setSelectedPreReg] = useState<PreRegistration | null>(null);
  const [approvalData, setApprovalData] = useState({
    authorized_by_resident_id: '',
    authorized_by_employee_id: '',
    authorized_by_name: '',
    vehicle_plate: '',
    access_type: 'single_visit' as 'single_visit' | 'multiple_visits',
    additional_notes: '',
    require_resident_approval: false,
  });
  const [isApproving, setIsApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [zoomedPhoto, setZoomedPhoto] = useState<{ url: string; name: string } | null>(null);

  // Rejection modal state
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  // QR code state
  const [activeQRCode, setActiveQRCode] = useState<QRCode | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  // Load pre-registrations
  const loadPreRegistrations = useCallback(async () => {
    const customerId = customerIdProp || selectedCustomer?.id;
    if (!customerId) {
      setError('Nenhum cliente selecionado');
      return;
    }

    console.log('loadPreRegistrations - loading for customer:', customerId);
    setIsLoading(true);
    setError(null);

    try {
      const data = await getPendingPreRegistrations(customerId);
      console.log('loadPreRegistrations - received data:', data);
      console.log('loadPreRegistrations - count:', data.length);
      setPreRegistrations(data);
    } catch (err) {
      console.error('Error loading pre-registrations:', err);
      setError('Erro ao carregar pré-registros');
    } finally {
      setIsLoading(false);
    }
  }, [customerIdProp, selectedCustomer?.id]);

  // Load active QR code
  const loadQRCode = useCallback(async () => {
    const customerId = customerIdProp || selectedCustomer?.id;
    if (!customerId) return;
    setQrLoading(true);
    try {
      // Try the dedicated active endpoint first
      let qr = await getActiveQRCode(customerId);

      // Fallback: if the dedicated endpoint returned null (e.g. porteiro 403),
      // try the list endpoint and pick the active one
      if (!qr) {
        const all = await getAllQRCodes(customerId);
        qr = all.find(q => q.is_active !== false && !q.deactivated_at) ?? null;
      }

      console.log('[PreRegistrationsPanel] loadQRCode result:', qr ? 'found' : 'null', { customerId });
      setActiveQRCode(qr);
    } catch (err) {
      console.error('[PreRegistrationsPanel] loadQRCode error:', err);
    } finally {
      setQrLoading(false);
    }
  }, [customerIdProp, selectedCustomer?.id]);

  // Load on mount and when panel opens
  useEffect(() => {
    if (open) {
      loadPreRegistrations();
      loadQRCode();
    }
  }, [open, loadPreRegistrations, loadQRCode]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!open || !autoRefresh) return;

    const interval = setInterval(() => {
      loadPreRegistrations();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [open, autoRefresh, loadPreRegistrations]);

  // Handle validate button click
  const handleValidateClick = (preReg: PreRegistration) => {
    if (onValidatePreRegistration) {
      onValidatePreRegistration(preReg);
    }
  };

  // Handle reject button click
  const handleRejectClick = (preReg: PreRegistration) => {
    setSelectedPreReg(preReg);
    setRejectionReason('');
    setRejectionError(null);
    setShowRejectionModal(true);
  };

  // Submit approval
  const handleApprovalSubmit = async () => {
    const activeCustomerId = customerIdProp || selectedCustomer?.id;
    if (!selectedPreReg || !activeCustomerId) return;

    setIsApproving(true);
    setApprovalError(null);

    try {
      await approvePreRegistration(activeCustomerId, selectedPreReg.id, approvalData);

      // Remove from pending list
      setPreRegistrations(prev => prev.filter(pr => pr.id !== selectedPreReg.id));

      // Close modal
      setShowApprovalModal(false);
      setSelectedPreReg(null);
    } catch (err: any) {
      console.error('Error approving pre-registration:', err);
      setApprovalError(err.message || 'Erro ao aprovar pré-registro');
    } finally {
      setIsApproving(false);
    }
  };

  // Submit rejection
  const handleRejectionSubmit = async () => {
    const activeCustomerId = customerIdProp || selectedCustomer?.id;
    if (!selectedPreReg || !activeCustomerId) return;

    if (!rejectionReason.trim()) {
      setRejectionError('Motivo da rejeição é obrigatório');
      return;
    }

    setIsRejecting(true);
    setRejectionError(null);

    try {
      await rejectPreRegistration(activeCustomerId, selectedPreReg.id, rejectionReason);

      // Remove from pending list
      setPreRegistrations(prev => prev.filter(pr => pr.id !== selectedPreReg.id));

      // Close modal
      setShowRejectionModal(false);
      setSelectedPreReg(null);
    } catch (err: any) {
      console.error('Error rejecting pre-registration:', err);
      setRejectionError(err.message || 'Erro ao rejeitar pré-registro');
    } finally {
      setIsRejecting(false);
    }
  };

  // Calculate time remaining until expiration
  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs < 0) return 'Expirado';
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}m`;
    return `${diffMinutes}m`;
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return 'Recentemente';
    }
  };

  // QR code URL helper
  const getQRCodeURL = (token: string) => {
    if (activeQRCode?.qr_code_url) return activeQRCode.qr_code_url;
    return `https://access.panther.com.br/pre-register?t=${token}`;
  };

  // Copy URL to clipboard
  const handleCopyURL = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {}
  };

  // Share via WhatsApp
  const handleShareWhatsApp = () => {
    if (!activeQRCode) return;
    const url = getQRCodeURL(activeQRCode.token);
    const customerName = selectedCustomer?.name || 'o condomínio';
    const message =
      `*Pré-Cadastro de Visitantes — ${customerName}*\n\n` +
      `Solicitamos que realize seu pré-cadastro com antecedência pelo link abaixo:\n\n` +
      `${url}\n\n` +
      `*Como funciona:*\n` +
      `1. Acesse o link acima\n` +
      `2. Preencha seus dados e envie uma foto de identificação\n` +
      `3. Aguarde a confirmação da portaria\n` +
      `4. Apresente-se na portaria para liberação de acesso\n\n` +
      `Em caso de dúvidas, entre em contato com a portaria.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Download QR code as PNG
  const handleDownloadPNG = () => {
    if (!activeQRCode || !qrCodeRef.current) return;
    const svg = qrCodeRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'qr-pre-registro.png';
        link.click();
        URL.revokeObjectURL(downloadUrl);
      });
    };
    img.src = svgUrl;
  };

  // Theme-aware color palette
  const c = {
    bg:              lightTheme ? '#ffffff'  : '#0a0a0a',
    surface:         lightTheme ? '#f8fafc'  : '#18181b',
    border:          lightTheme ? '#e2e8f0'  : '#27272a',
    inputBorder:     lightTheme ? '#cbd5e1'  : '#3f3f46',
    inputBorderHov:  lightTheme ? '#94a3b8'  : '#52525b',
    iconMuted:       lightTheme ? '#94a3b8'  : '#52525b',
    textMuted:       lightTheme ? '#64748b'  : '#71717a',
    textSecondary:   lightTheme ? '#475569'  : '#a1a1aa',
    textPrimary:     lightTheme ? '#0f172a'  : '#e4e4e7',
    chipBg:          lightTheme ? '#f1f5f9'  : '#27272a',
    codeboxBg:       lightTheme ? '#f1f5f9'  : '#0a0a0a',
    commentBg:       lightTheme ? '#f1f5f9'  : '#27272a',
    disabledBtn:     lightTheme ? '#e2e8f0'  : '#27272a',
    disabledText:    lightTheme ? '#94a3b8'  : '#52525b',
  };

  // Theme-aware field sx for TextField / FormControl
  const darkFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: c.textPrimary,
      '& fieldset': { borderColor: c.inputBorder },
      '&:hover fieldset': { borderColor: c.inputBorderHov },
      '&.Mui-focused fieldset': { borderColor: '#2563eb' },
    },
    '& .MuiInputLabel-root': { color: c.textSecondary },
    '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' },
    '& .MuiSelect-icon': { color: c.textMuted },
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop - only in overlay mode */}
      {!inline && (
        <Box
          onClick={onClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: { xs: 0, sm: 450 },
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1299,
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Main Panel — fixed overlay or inline fill */}
      <Paper
        elevation={inline ? 0 : 24}
        sx={inline ? {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: c.bg,
          height: '100%',
          width: '100%',
          borderRadius: 0,
        } : {
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: '100%', sm: 450 },
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.5)',
          bgcolor: c.bg,
        }}
      >
        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* QR Code Section */}
          {qrLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeQRCode ? (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: c.surface,
                border: `1px solid ${c.border}`,
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <QrCodeIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                <Typography variant="subtitle2" sx={{ color: c.textPrimary, fontWeight: 600, flex: 1 }}>
                  Link de Pré-Registro
                </Typography>
                <Tooltip title="Atualizar lista">
                  <IconButton size="small" onClick={loadPreRegistrations} sx={{ color: c.textMuted }}>
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {/* QR Code image */}
                <Box
                  ref={qrCodeRef}
                  sx={{ flexShrink: 0, bgcolor: '#fff', p: 1, borderRadius: 1 }}
                >
                  <QRCodeSVG
                    value={getQRCodeURL(activeQRCode.token)}
                    size={120}
                    level="M"
                  />
                </Box>

                {/* URL + stats + download */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: c.textMuted, display: 'block', mb: 0.5 }}>
                    URL de acesso
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 1,
                        bgcolor: c.codeboxBg,
                        border: `1px solid ${c.inputBorder}`,
                        borderRadius: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: c.textSecondary,
                          fontFamily: 'monospace',
                          fontSize: '0.65rem',
                          wordBreak: 'break-all',
                          display: 'block',
                        }}
                      >
                        {getQRCodeURL(activeQRCode.token)}
                      </Typography>
                    </Box>
                    <Tooltip title={copySuccess ? 'Copiado!' : 'Copiar link'}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyURL(getQRCodeURL(activeQRCode.token))}
                        sx={{ color: copySuccess ? '#4ade80' : c.textMuted, flexShrink: 0 }}
                      >
                        <CopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Usage stats */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${activeQRCode.uses_today}/${activeQRCode.max_uses_per_day ?? '∞'} hoje`}
                      size="small"
                      sx={{ bgcolor: c.chipBg, color: c.textSecondary, fontSize: '0.7rem', height: 20 }}
                    />
                    {activeQRCode.total_uses !== undefined && (
                      <Chip
                        label={`${activeQRCode.total_uses} total`}
                        size="small"
                        sx={{ bgcolor: c.chipBg, color: c.textSecondary, fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>

                  {/* Download + WhatsApp + Novo Pré-Registro buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                      onClick={handleDownloadPNG}
                      sx={{
                        borderColor: c.inputBorder,
                        color: c.textSecondary,
                        fontSize: '0.72rem',
                        py: 0.5,
                        '&:hover': { borderColor: '#2563eb', color: '#60a5fa', bgcolor: 'transparent' },
                      }}
                    >
                      Baixar
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                      onClick={handleShareWhatsApp}
                      sx={{
                        bgcolor: '#25D366',
                        color: '#fff',
                        fontSize: '0.72rem',
                        py: 0.5,
                        '&:hover': { bgcolor: '#1ebe57' },
                        boxShadow: 'none',
                      }}
                    >
                      WhatsApp
                    </Button>
                    {onNewRegistration && activeQRCode && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                        onClick={() => activeQRCode && onNewRegistration?.(activeQRCode.token)}
                        sx={{
                          bgcolor: '#2563eb',
                          color: '#fff',
                          fontSize: '0.72rem',
                          py: 0.5,
                          '&:hover': { bgcolor: '#1d4ed8' },
                          boxShadow: 'none',
                        }}
                      >
                        Novo Registro
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Slim toolbar: pending count + auto-refresh status */}
          {!isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: c.textMuted }}>
                {preRegistrations.length > 0
                  ? `${preRegistrations.length} pré-registro${preRegistrations.length > 1 ? 's' : ''} pendente${preRegistrations.length > 1 ? 's' : ''}`
                  : 'Nenhum pendente'}
              </Typography>
              <Typography variant="caption" sx={{ color: c.iconMuted, fontSize: '0.65rem' }}>
                {autoRefresh ? '↻ auto 30s' : ''}
              </Typography>
            </Box>
          )}

          {isLoading && preRegistrations.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : preRegistrations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PersonIcon sx={{ fontSize: 64, color: c.iconMuted, mb: 2 }} />
              <Typography variant="body1" sx={{ color: c.textSecondary }}>
                Nenhum pré-registro pendente
              </Typography>
              <Typography variant="caption" sx={{ color: c.textMuted }}>
                Novos pré-registros aparecerão aqui
              </Typography>
            </Box>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {preRegistrations.map((preReg) => (
                <div
                  key={preReg.id}
                  className={`group rounded-xl border overflow-hidden flex flex-col transition-all hover:shadow-md ${
                    lightTheme
                      ? 'bg-white border-gray-200 hover:border-gray-300'
                      : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  {/* Photo */}
                  <div
                    className={`w-full aspect-square relative flex items-center justify-center overflow-hidden ${lightTheme ? 'bg-gray-100' : 'bg-zinc-800'} ${preReg.profile_picture_url ? 'cursor-zoom-in' : ''}`}
                    onClick={() => preReg.profile_picture_url && setZoomedPhoto({ url: preReg.profile_picture_url, name: `${preReg.first_name} ${preReg.last_name}` })}
                  >
                    {preReg.profile_picture_url ? (
                      <>
                        <img
                          src={getAbsoluteImageUrl(preReg.profile_picture_url)}
                          alt={`${preReg.first_name} ${preReg.last_name}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                        </div>
                      </>
                    ) : (
                      <span className={`text-3xl font-bold select-none ${lightTheme ? 'text-gray-300' : 'text-zinc-600'}`}>
                        {(preReg.first_name?.[0] ?? '').toUpperCase()}{(preReg.last_name?.[0] ?? '').toUpperCase()}
                      </span>
                    )}
                    {/* Expiry overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5 text-amber-300 flex-shrink-0" />
                        <span className="text-[10px] font-semibold text-amber-300 leading-none">
                          {getTimeRemaining(preReg.expires_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-3 flex flex-col gap-1.5 flex-1 min-w-0">
                    <p className={`text-sm font-semibold leading-tight truncate ${lightTheme ? 'text-gray-900' : 'text-zinc-100'}`}>
                      {preReg.first_name} {preReg.last_name}
                    </p>
                    <div className={`flex items-center gap-1 text-xs min-w-0 ${lightTheme ? 'text-gray-500' : 'text-zinc-400'}`}>
                      <Home className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{preReg.visiting_block_name} · Un. {preReg.visiting_unit_number}</span>
                    </div>
                    {preReg.phone && (
                      <div className={`flex items-center gap-1 text-xs min-w-0 ${lightTheme ? 'text-gray-500' : 'text-zinc-400'}`}>
                        <Phone className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{preReg.phone}</span>
                      </div>
                    )}
                    {preReg.comment && (
                      <p className={`text-[11px] italic line-clamp-2 leading-snug ${lightTheme ? 'text-gray-400' : 'text-zinc-500'}`}>
                        "{preReg.comment}"
                      </p>
                    )}
                    <p className={`text-[10px] mt-auto pt-1 ${lightTheme ? 'text-gray-400' : 'text-zinc-600'}`}>
                      {formatRelativeTime(preReg.created_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="px-3 pb-3 flex gap-1.5">
                    <button
                      onClick={() => handleValidateClick(preReg)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Validar
                    </button>
                    <button
                      onClick={() => handleRejectClick(preReg)}
                      className={`flex items-center justify-center px-2.5 py-1.5 rounded-lg transition-colors ${
                        lightTheme
                          ? 'text-red-600 bg-red-50 hover:bg-red-100'
                          : 'text-red-400 bg-red-950/50 hover:bg-red-900/50'
                      }`}
                      title="Rejeitar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Box>

      </Paper>

      {/* Approval Modal */}
      <Dialog
        open={showApprovalModal}
        onClose={() => !isApproving && setShowApprovalModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: c.surface,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ color: c.textPrimary, borderBottom: `1px solid ${c.border}` }}>
          Aprovar Pré-Registro
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreReg && (
            <Box>
              {/* Visitor Info */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={getAbsoluteImageUrl(selectedPreReg.profile_picture_url)} sx={{ width: 60, height: 60 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1rem', color: c.textPrimary }}>
                    {selectedPreReg.first_name} {selectedPreReg.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textSecondary }}>
                    {selectedPreReg.visiting_block_name} - Unidade {selectedPreReg.visiting_unit_number}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3, borderColor: c.border }} />

              {approvalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {approvalError}
                </Alert>
              )}

              {/* Approval Form */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Autorizado por (Morador ID)"
                  value={approvalData.authorized_by_resident_id}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, authorized_by_resident_id: e.target.value }))}
                  fullWidth
                  placeholder="ID do morador que autorizou"
                  helperText="Opcional - ID do morador que autorizou a visita"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: c.textPrimary,
                      '& fieldset': { borderColor: c.inputBorder },
                      '&:hover fieldset': { borderColor: c.inputBorderHov },
                      '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
                    },
                    '& .MuiInputLabel-root': { color: c.textSecondary },
                    '& .MuiFormHelperText-root': { color: c.textMuted },
                  }}
                />

                <TextField
                  label="Placa do Veículo"
                  value={approvalData.vehicle_plate}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, vehicle_plate: e.target.value.toUpperCase() }))}
                  fullWidth
                  placeholder="ABC-1234"
                  helperText="Opcional - Placa do veículo do visitante"
                />

                <FormControl fullWidth>
                  <InputLabel>Tipo de Acesso</InputLabel>
                  <Select
                    value={approvalData.access_type}
                    label="Tipo de Acesso"
                    onChange={(e) => setApprovalData(prev => ({ ...prev, access_type: e.target.value as any }))}
                  >
                    <MenuItem value="single_visit">Visita Única</MenuItem>
                    <MenuItem value="multiple_visits">Múltiplas Visitas</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Observações Adicionais"
                  value={approvalData.additional_notes}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, additional_notes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Observações sobre a aprovação..."
                  helperText="Opcional - Notas adicionais do porteiro"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: c.bg, borderTop: `1px solid ${c.border}` }}>
          <Button onClick={() => setShowApprovalModal(false)} disabled={isApproving} sx={{ color: c.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleApprovalSubmit}
            disabled={isApproving}
            startIcon={isApproving ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{
              bgcolor: '#166534',
              color: '#f0fdf4',
              '&:hover': { bgcolor: '#15803d' }
            }}
          >
            {isApproving ? 'Aprovando...' : 'Aprovar'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Rejection Modal */}
      <Dialog
        open={showRejectionModal}
        onClose={() => !isRejecting && setShowRejectionModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: c.surface,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ color: c.textPrimary, borderBottom: `1px solid ${c.border}` }}>
          Rejeitar Pré-Registro
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: c.border, bgcolor: c.surface }}>
          {selectedPreReg && (
            <Box>
              {/* Visitor Info */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={getAbsoluteImageUrl(selectedPreReg.profile_picture_url)} sx={{ width: 60, height: 60 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1rem', color: c.textPrimary }}>
                    {selectedPreReg.first_name} {selectedPreReg.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textSecondary }}>
                    {selectedPreReg.visiting_block_name} - Unidade {selectedPreReg.visiting_unit_number}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3, borderColor: c.border }} />

              {rejectionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {rejectionError}
                </Alert>
              )}

              {/* Rejection Form */}
              <TextField
                label="Motivo da Rejeição"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                placeholder="Explique o motivo da rejeição..."
                helperText="Obrigatório - Motivo será registrado no sistema"
                autoFocus
                sx={darkFieldSx}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: c.bg, borderTop: `1px solid ${c.border}` }}>
          <Button onClick={() => setShowRejectionModal(false)} disabled={isRejecting} sx={{ color: c.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectionSubmit}
            disabled={isRejecting || !rejectionReason.trim()}
            startIcon={isRejecting ? <CircularProgress size={20} /> : <RejectIcon />}
            sx={{
              bgcolor: '#7f1d1d',
              color: '#fca5a5',
              '&:hover': { bgcolor: '#991b1b' }
            }}
          >
            {isRejecting ? 'Rejeitando...' : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo lightbox */}
      {zoomedPhoto && (
        <div
          className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            onClick={() => setZoomedPhoto(null)}
          >
            <X className="h-7 w-7" />
          </button>
          <img
            src={zoomedPhoto.url}
            alt={zoomedPhoto.name}
            className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white font-semibold text-lg drop-shadow-lg whitespace-nowrap">
            {zoomedPhoto.name}
          </p>
        </div>
      )}
    </>
  );
};

export default PreRegistrationsPanel;
